import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../client/http-client';
import { queryKeys } from '../utils/query-keys';

export function useRecipeComments(recipeId: string) {
  return useQuery({
    queryKey: queryKeys.comments.recipe(recipeId),
    queryFn: () => apiClient.getRecipeComments(recipeId),
    enabled: !!recipeId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

export function useCreateComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      recipeId,
      content,
    }: {
      recipeId: string;
      content: string;
    }) => apiClient.createRecipeComment(recipeId, content),
    onSuccess: (_, { recipeId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.comments.recipe(recipeId),
      });
    },
  });
}

export function useReplyToComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      recipeId,
      commentId,
      content,
    }: {
      recipeId: string;
      commentId: string;
      content: string;
    }) => apiClient.replyToComment(recipeId, commentId, content),
    onSuccess: () => {
      // Invalidate all comment queries since we don't know which recipe this belongs to
      queryClient.invalidateQueries({
        queryKey: queryKeys.comments.all,
      });
    },
  });
}

export function useLikeComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (commentId: string) => apiClient.likeComment(commentId),
    onMutate: async (commentId: string) => {
      // Cancel any outgoing refetches to avoid overwriting optimistic update
      await queryClient.cancelQueries({ queryKey: queryKeys.comments.all });

      // Snapshot the previous value
      const previousComments = queryClient.getQueriesData({
        queryKey: queryKeys.comments.all,
      });

      // Optimistically update all comment queries
      queryClient.setQueriesData(
        { queryKey: queryKeys.comments.all },
        (old: any) => {
          if (!old?.comments) return old;

          return {
            ...old,
            comments: old.comments.map((comment: any) =>
              comment.id === commentId
                ? {
                    ...comment,
                    is_liked_by_user: true,
                    likes_count: (comment.likes_count || 0) + 1,
                  }
                : comment
            ),
          };
        }
      );

      return { previousComments };
    },
    onError: (_err, _commentId, context) => {
      // Rollback on error
      if (context?.previousComments) {
        context.previousComments.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    // Don't refetch immediately - trust the optimistic update
    // The next natural refetch will sync with server
  });
}

export function useUnlikeComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (commentId: string) => apiClient.unlikeComment(commentId),
    onMutate: async (commentId: string) => {
      // Cancel any outgoing refetches to avoid overwriting optimistic update
      await queryClient.cancelQueries({ queryKey: queryKeys.comments.all });

      // Snapshot the previous value
      const previousComments = queryClient.getQueriesData({
        queryKey: queryKeys.comments.all,
      });

      // Optimistically update all comment queries
      queryClient.setQueriesData(
        { queryKey: queryKeys.comments.all },
        (old: any) => {
          if (!old?.comments) return old;

          return {
            ...old,
            comments: old.comments.map((comment: any) =>
              comment.id === commentId
                ? {
                    ...comment,
                    is_liked_by_user: false,
                    likes_count: Math.max((comment.likes_count || 0) - 1, 0),
                  }
                : comment
            ),
          };
        }
      );

      return { previousComments };
    },
    onError: (_err, _commentId, context) => {
      // Rollback on error
      if (context?.previousComments) {
        context.previousComments.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    // Don't refetch immediately - trust the optimistic update
    // The next natural refetch will sync with server
  });
}

export function useUpdateComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      commentId,
      content,
    }: {
      commentId: string;
      content: string;
    }) => apiClient.updateComment(commentId, content),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.comments.all,
      });
    },
  });
}

export function useDeleteComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (commentId: string) => apiClient.deleteComment(commentId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.comments.all,
      });
    },
  });
}

export function useUserComments(params?: { page?: number; size?: number }) {
  return useQuery({
    queryKey: queryKeys.comments.user(params),
    queryFn: () => apiClient.getUserComments(params),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}
